import { useEffect, useState, useCallback } from "react";
import api, { apiErr, isPaywall } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Container, PageHeader, Spinner, PaywallNotice } from "@/components/shared";
import { MicButton } from "@/components/MicButton";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Mail, Plus, ArrowLeft, Copy, Download, Save, Sparkles, Trash2, CopyPlus,
  FileText, ShieldCheck, ListChecks, Building2, Info,
} from "lucide-react";

const empty = {
  recipient_key: "", recipient_custom: "", letter_type: "", tone: "courtois",
  situation: "", lieu: "", date_incident: "", personnes: "", temoins: "", preuves: "", objectif: "",
};

export default function Letters() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [opts, setOpts] = useState(null);
  const [items, setItems] = useState([]);
  const [view, setView] = useState("list"); // list | form | result
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [result, setResult] = useState(null); // {objet, corps, recipient_label, conseils, ...}
  const [currentId, setCurrentId] = useState(null);

  const hasSender = !!(user?.sender_info?.nom || user?.sender_info?.prenom);

  const loadList = useCallback(async () => {
    try { const { data } = await api.get("/letters"); setItems(data.items); } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { api.get("/letters/options").then(({ data }) => setOpts(data)).catch(() => {}); loadList(); }, [loadList]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    if (!form.situation.trim()) { toast.error(t("pages.letters.needSituation")); return; }
    setBusy(true); setPaywall(false);
    try {
      const { data } = await api.post("/letters/generate", form);
      setResult(data); setCurrentId(null); setView("result"); window.scrollTo(0, 0);
    } catch (e) { if (isPaywall(e)) setPaywall(true); else toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      if (currentId) {
        await api.put(`/letters/${currentId}`, { objet: result.objet, corps: result.corps, recipient_label: result.recipient_label });
      } else {
        const { data } = await api.post("/letters", {
          recipient_label: result.recipient_label, recipient_key: result.recipient_key,
          letter_type: result.letter_type, tone: result.tone,
          objet: result.objet, corps: result.corps, conseils: result.conseils,
        });
        setCurrentId(data.letter_id);
      }
      await loadList();
      toast.success(t("pages.letters.saved"));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
    return true;
  };

  const downloadPdf = async () => {
    let id = currentId;
    if (!id) {
      setBusy(true);
      try {
        const { data } = await api.post("/letters", {
          recipient_label: result.recipient_label, recipient_key: result.recipient_key,
          letter_type: result.letter_type, tone: result.tone,
          objet: result.objet, corps: result.corps, conseils: result.conseils,
        });
        id = data.letter_id; setCurrentId(id); await loadList();
      } catch (e) { toast.error(apiErr(e)); setBusy(false); return; } finally { setBusy(false); }
    }
    try {
      const res = await api.get(`/letters/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `courrier-${id}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible."); }
  };

  const copyText = () => {
    const t2 = `Objet : ${result.objet}\n\n${result.corps}`;
    navigator.clipboard.writeText(t2); toast.success(t("pages.letters.copied"));
  };

  const openLetter = (l) => { setResult(l); setCurrentId(l.letter_id); setView("result"); window.scrollTo(0, 0); };
  const duplicate = async (id) => { try { await api.post(`/letters/${id}/duplicate`); await loadList(); toast.success(t("pages.letters.duplicated")); } catch (e) { toast.error(apiErr(e)); } };
  const remove = async (id) => { if (!window.confirm(t("pages.letters.confirmRemove"))) return; try { await api.delete(`/letters/${id}`); await loadList(); toast.success(t("pages.letters.removed")); } catch (e) { toast.error(apiErr(e)); } };
  const startNew = () => { setForm(empty); setResult(null); setCurrentId(null); setView("form"); window.scrollTo(0, 0); };

  // ---------------- RESULT (éditeur) ----------------
  if (view === "result" && result) {
    return (
      <Container>
        <button data-testid="letters-back-list" onClick={() => setView("list")} className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-2 mb-5"><ArrowLeft size={16} /> {t("pages.letters.back")}</button>
        <PageHeader eyebrow={t("pages.letters.resultEyebrow")} title={t("pages.letters.resultTitle")} subtitle={t("pages.letters.recipientPrefix") + result.recipient_label} />

        <div className="card-surface p-5 mb-4">
          <label className="text-white/50 text-xs uppercase tracking-wide">{t("pages.letters.objet")}</label>
          <input data-testid="letter-objet" value={result.objet} onChange={(e) => setResult({ ...result, objet: e.target.value })}
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm mt-1 mb-4" />
          <label className="text-white/50 text-xs uppercase tracking-wide">{t("pages.letters.corps")}</label>
          <textarea data-testid="letter-corps" value={result.corps} onChange={(e) => setResult({ ...result, corps: e.target.value })}
            rows={16} className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-3 outline-none text-sm leading-relaxed mt-1 resize-y" />
          <div className="mt-2"><MicButton testid="letter-corps-mic" value={result.corps} onChange={(v) => setResult((r) => ({ ...r, corps: v }))} /></div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          <button data-testid="letter-copy" onClick={copyText} className="rounded-full border border-white/15 px-4 py-2 text-sm inline-flex items-center gap-2 hover:border-gold/40 hover:text-gold"><Copy size={15} /> {t("pages.letters.copy")}</button>
          <button data-testid="letter-save" onClick={save} disabled={busy} className="rounded-full border border-white/15 px-4 py-2 text-sm inline-flex items-center gap-2 hover:border-gold/40 hover:text-gold disabled:opacity-50"><Save size={15} /> {t("pages.letters.save")}</button>
          <button data-testid="letter-pdf" onClick={downloadPdf} disabled={busy} className="gold-gradient text-black rounded-full px-5 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50"><Download size={15} /> {t("pages.letters.pdf")}</button>
        </div>

        {!hasSender && (
          <div className="card-surface p-4 mb-6 flex gap-3 border-gold/25" data-testid="letter-sender-warning">
            <Info size={18} className="text-gold shrink-0 mt-0.5" />
            <p className="text-white/70 text-sm">{t("pages.letters.senderWarn1")}<Link to="/profile" className="text-gold underline">{t("pages.letters.senderWarnLink")}</Link>{t("pages.letters.senderWarn2")}</p>
          </div>
        )}

        {result.conseils && (result.conseils.preuves?.length || result.conseils.etapes?.length || result.conseils.organismes?.length) ? (
          <div className="grid md:grid-cols-3 gap-4" data-testid="letter-conseils">
            <Advice icon={ShieldCheck} title={t("pages.letters.advPreuves")} list={result.conseils.preuves} />
            <Advice icon={ListChecks} title={t("pages.letters.advEtapes")} list={result.conseils.etapes} />
            <Advice icon={Building2} title={t("pages.letters.advOrgs")} list={result.conseils.organismes} />
          </div>
        ) : null}
      </Container>
    );
  }

  // ---------------- FORM (assistant) ----------------
  if (view === "form") {
    if (!opts) return <Container><Spinner label={t("pages.letters.loading")} /></Container>;
    return (
      <Container>
        <button data-testid="letters-back-list-form" onClick={() => setView("list")} className="text-white/50 hover:text-gold text-sm inline-flex items-center gap-2 mb-5"><ArrowLeft size={16} /> {t("pages.letters.back")}</button>
        <PageHeader eyebrow={t("pages.letters.formEyebrow")} title={t("pages.letters.formTitle")} subtitle={t("pages.letters.formSub")} />

        <div className="card-surface p-5 space-y-4">
          <Field label={t("pages.letters.toWhom")}>
            <select data-testid="letter-recipient" value={form.recipient_key} onChange={(e) => set("recipient_key", e.target.value)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm">
              <option value="">{t("pages.letters.chooseRecipient")}</option>
              {opts.recipients.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <input data-testid="letter-recipient-custom" value={form.recipient_custom} onChange={(e) => set("recipient_custom", e.target.value)}
              placeholder={t("pages.letters.orCustom")}
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm mt-2" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label={t("pages.letters.letterType")}>
              <select data-testid="letter-type" value={form.letter_type} onChange={(e) => set("letter_type", e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm">
                <option value="">{t("pages.letters.optional")}</option>
                {opts.types.map((ty) => <option key={ty.key} value={ty.key}>{ty.label}</option>)}
              </select>
            </Field>
            <Field label={t("pages.letters.tone")}>
              <select data-testid="letter-tone" value={form.tone} onChange={(e) => set("tone", e.target.value)}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm">
                {opts.tones.map((ty) => <option key={ty.key} value={ty.key}>{ty.label} — {ty.desc}</option>)}
              </select>
            </Field>
          </div>

          {opts.questions.map((qn) => (
            <Field key={qn.key} label={qn.label}>
              {qn.type === "textarea" ? (
                <div>
                  <textarea data-testid={`letter-q-${qn.key}`} value={form[qn.key]} onChange={(e) => set(qn.key, e.target.value)}
                    rows={3} placeholder={qn.placeholder}
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm resize-y" />
                  <div className="mt-2"><MicButton testid={`letter-q-mic-${qn.key}`} value={form[qn.key]} onChange={(v) => setForm((f) => ({ ...f, [qn.key]: v }))} /></div>
                </div>
              ) : (
                <input data-testid={`letter-q-${qn.key}`} value={form[qn.key]} onChange={(e) => set(qn.key, e.target.value)}
                  placeholder={qn.placeholder}
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3 py-2.5 outline-none text-sm" />
              )}
            </Field>
          ))}

          {paywall && <PaywallNotice />}
          <button data-testid="letter-generate" onClick={generate} disabled={busy}
            className="w-full gold-gradient text-black rounded-2xl px-6 py-3.5 font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
            <Sparkles size={18} /> {busy ? t("pages.letters.generating") : t("pages.letters.generate")}
          </button>
          {busy && <Spinner label={t("pages.letters.spinnerGen")} />}
        </div>
      </Container>
    );
  }

  // ---------------- LIST (historique) ----------------
  return (
    <Container>
      <PageHeader eyebrow={t("pages.letters.listEyebrow")} title={t("pages.letters.listTitle")} subtitle={t("pages.letters.listSub")} />
      <button data-testid="letter-new" onClick={startNew} className="gold-gradient text-black rounded-2xl px-5 py-3 font-medium inline-flex items-center gap-2 mb-8"><Plus size={18} /> {t("pages.letters.newLetter")}</button>

      {items.length === 0 ? (
        <div className="card-surface p-10 text-center" data-testid="letters-empty">
          <Mail size={32} className="text-gold/70 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-white/60 text-sm">{t("pages.letters.empty")}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {items.map((l) => (
            <div key={l.letter_id} data-testid={`letter-card-${l.letter_id}`} className="card-surface p-5">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0"><FileText size={18} className="text-gold" strokeWidth={1.5} /></div>
                <div className="min-w-0">
                  <button onClick={() => openLetter(l)} className="text-left font-display text-base hover:text-gold transition-colors line-clamp-2">{l.objet}</button>
                  <p className="text-white/40 text-xs mt-0.5">{l.recipient_label}</p>
                </div>
              </div>
              <p className="text-white/45 text-sm line-clamp-2 mb-3">{l.corps}</p>
              <div className="flex flex-wrap gap-2">
                <button data-testid={`letter-open-${l.letter_id}`} onClick={() => openLetter(l)} className="text-xs rounded-full border border-white/15 px-3 py-1.5 hover:border-gold/40 hover:text-gold">{t("pages.letters.open")}</button>
                <button onClick={() => duplicate(l.letter_id)} className="text-xs rounded-full border border-white/15 px-3 py-1.5 hover:border-gold/40 hover:text-gold inline-flex items-center gap-1"><CopyPlus size={13} /> {t("pages.letters.duplicate")}</button>
                <button onClick={() => remove(l.letter_id)} className="text-xs rounded-full border border-white/15 px-3 py-1.5 hover:border-red-400/40 hover:text-red-300 inline-flex items-center gap-1"><Trash2 size={13} /> {t("pages.letters.remove")}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Container>
  );
}

const Field = ({ label, children }) => (
  <div><label className="text-white/60 text-sm block mb-1.5">{label}</label>{children}</div>
);

const Advice = ({ icon: Icon, title, list }) =>
  (list && list.length) ? (
    <div className="card-surface p-4">
      <div className="flex items-center gap-2 mb-2"><Icon size={16} className="text-gold" /><p className="text-sm font-medium">{title}</p></div>
      <ul className="space-y-1">{list.map((x, i) => <li key={i} className="text-white/70 text-sm flex gap-2"><span className="text-gold/60">—</span>{x}</li>)}</ul>
    </div>
  ) : null;
